import { Controller } from "@hotwired/stimulus"

// Multi-date calendar picker for specific_dates recurrence type.
// Renders a small month grid; clicking a date toggles its selection.
// Selected dates sync to a hidden input as comma-joined ISO strings.
export default class extends Controller {
  static targets = ["hidden", "calendar", "monthLabel", "chips", "timingHidden"]
  static values = {
    dates: Array,
    timingMode: { type: String, default: "fixed" },
    locale: { type: String, default: "nl" },
    emptyLabel: { type: String, default: "Geen datums geselecteerd" },
    removeLabel: { type: String, default: "Verwijder" }
  }

  connect() {
    this.selected = new Set(this.datesValue.filter(Boolean))
    this.timingMode = this.timingModeValue || "fixed"

    const firstSelected = Array.from(this.selected).sort()[0]
    if (firstSelected) {
      const d = new Date(firstSelected + "T12:00:00")
      this.year = d.getFullYear()
      this.month = d.getMonth()
    } else {
      const now = new Date()
      this.year = now.getFullYear()
      this.month = now.getMonth()
    }

    this.render()
    this.syncTimingHidden()
  }

  prevMonth() {
    if (--this.month < 0) { this.month = 11; this.year-- }
    this.renderCalendar()
  }

  nextMonth() {
    if (++this.month > 11) { this.month = 0; this.year++ }
    this.renderCalendar()
  }

  toggleDate(event) {
    const iso = event.currentTarget.dataset.date
    if (!iso) return
    this.selected.has(iso) ? this.selected.delete(iso) : this.selected.add(iso)
    this.syncHidden()
    this.renderCalendar()
    this.renderChips()
  }

  removeChip(event) {
    this.selected.delete(event.currentTarget.dataset.date)
    this.syncHidden()
    this.renderCalendar()
    this.renderChips()
  }

  setTiming(event) {
    this.timingMode = event.currentTarget.value
    this.syncTimingHidden()
  }

  // ── render ──────────────────────────────────────────────────────────────

  render() {
    this.renderCalendar()
    this.renderChips()
  }

  renderCalendar() {
    this.renderMonthLabel()
    this.renderDays()
  }

  renderMonthLabel() {
    const label = new Date(this.year, this.month, 1)
      .toLocaleDateString(this.bcp47, { month: "long", year: "numeric" })
    this.monthLabelTarget.textContent = label.charAt(0).toUpperCase() + label.slice(1)
  }

  renderDays() {
    const grid = this.calendarTarget
    grid.innerHTML = ""

    // Day-of-week headers, localized (ISO: Monday first). 2024-01-01 is a Monday.
    const headFmt = new Intl.DateTimeFormat(this.bcp47, { weekday: "short" })
    for (let i = 0; i < 7; i++) {
      const h = document.createElement("div")
      h.className = "text-center text-xs font-medium text-gray-400 py-1"
      h.textContent = headFmt.format(new Date(2024, 0, 1 + i))
      grid.appendChild(h)
    }

    // Leading blank cells (Monday = 0)
    const firstDow = (new Date(this.year, this.month, 1).getDay() + 6) % 7
    for (let i = 0; i < firstDow; i++) {
      grid.appendChild(document.createElement("div"))
    }

    const todayISO = this.isoDate(new Date())
    const daysInMonth = new Date(this.year, this.month + 1, 0).getDate()

    for (let day = 1; day <= daysInMonth; day++) {
      const iso = this.isoDate(new Date(this.year, this.month, day))
      const isSelected = this.selected.has(iso)
      const isToday = iso === todayISO

      const btn = document.createElement("button")
      btn.type = "button"
      btn.dataset.date = iso
      btn.dataset.action = "click->date-picker#toggleDate"
      btn.textContent = day

      if (isSelected) {
        btn.className = "text-sm rounded-full w-8 h-8 mx-auto flex items-center justify-center bg-[var(--pu-magenta)] text-white font-semibold cursor-pointer"
      } else if (isToday) {
        btn.className = "text-sm rounded-full w-8 h-8 mx-auto flex items-center justify-center text-[var(--pu-magenta)] font-semibold border border-[var(--pu-magenta)] hover:bg-pink-50 cursor-pointer"
      } else {
        btn.className = "text-sm rounded-full w-8 h-8 mx-auto flex items-center justify-center text-gray-700 hover:bg-gray-100 cursor-pointer"
      }

      grid.appendChild(btn)
    }
  }

  renderChips() {
    const container = this.chipsTarget
    container.innerHTML = ""

    if (this.selected.size === 0) {
      const empty = document.createElement("span")
      empty.className = "text-xs text-gray-400 italic"
      empty.textContent = this.emptyLabelValue
      container.appendChild(empty)
      return
    }

    Array.from(this.selected).sort().forEach(iso => {
      const label = new Date(iso + "T12:00:00")
        .toLocaleDateString(this.bcp47, { weekday: "short", day: "numeric", month: "short" })

      const chip = document.createElement("span")
      chip.className = "inline-flex items-center gap-1 bg-pink-50 border border-pink-200 text-pink-800 text-xs rounded-full px-2.5 py-0.5"
      chip.append(label)

      const remove = document.createElement("button")
      remove.type = "button"
      remove.dataset.date = iso
      remove.dataset.action = "click->date-picker#removeChip"
      remove.className = "ml-1 text-pink-400 hover:text-pink-700 leading-none"
      remove.setAttribute("aria-label", this.removeLabelValue)
      remove.textContent = "×"
      chip.appendChild(remove)

      container.appendChild(chip)
    })
  }

  // ── sync ────────────────────────────────────────────────────────────────

  syncHidden() {
    this.hiddenTarget.value = Array.from(this.selected).sort().join(",")
  }

  syncTimingHidden() {
    if (this.hasTimingHiddenTarget) this.timingHiddenTarget.value = this.timingMode
  }

  // ── helpers ─────────────────────────────────────────────────────────────

  get bcp47() {
    return { nl: "nl-NL", en: "en-GB", de: "de-DE" }[this.localeValue] || "nl-NL"
  }

  isoDate(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
  }
}
